import { ContactSearchField } from "@/features/contacts";
import Button from "@imminently/decisively-ui/Button";
import Collapsible from "@imminently/decisively-ui/Collapsible";
import Label from "@imminently/decisively-ui/Label";
import Select, { type SelectOptions } from "@imminently/decisively-ui/Select";
import SwitchField from "@imminently/decisively-ui/SwitchField";
import TextInput from "@imminently/decisively-ui/TextInput";
import type React from "react";
import { TYPE_OPTIONS, type ListFilters } from "../types";

const TYPE_SELECT_OPTIONS: SelectOptions = [
  { value: "", label: "All" },
  ...TYPE_OPTIONS.map((type) => ({ value: type, label: type })),
];

type OrgModelFiltersProps = {
  filters: ListFilters;
  onFiltersChange: (filters: ListFilters) => void;
  relatedIdContactSearch: boolean;
  onToggleRelatedIdContactSearch: () => void;
  onApply: () => void;
  onClear: () => void;
};

export const OrgModelFilters = (props: OrgModelFiltersProps) => {
  const { filters, onFiltersChange, relatedIdContactSearch, onToggleRelatedIdContactSearch, onApply, onClear } =
    props;

  const setField = <K extends keyof ListFilters>(field: K, value: ListFilters[K]) => {
    onFiltersChange({ ...filters, [field]: value });
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    onApply();
  };

  return (
    <Collapsible className="border-b border-border" defaultCollapsed={false}>
      <Collapsible.Header className="border-none">Filters</Collapsible.Header>
      <Collapsible.Content
        className="grid grid-cols-1 gap-2 px-4 pb-4"
        onKeyDown={onKeyDown}
      >
        <div>
          <Label>Type</Label>
          <Select
            value={filters.type}
            onValueChange={(value) => setField("type", (value ?? "") as ListFilters["type"])}
            options={TYPE_SELECT_OPTIONS}
          />
        </div>
        <div>
          <Label>Subject Type</Label>
          <TextInput
            fullWidth
            value={filters.subjectType}
            onValueChange={(value) => setField("subjectType", value)}
          />
        </div>
        <div>
          <Label>Subject ID</Label>
          <TextInput
            fullWidth
            value={filters.subjectId}
            onValueChange={(value) => setField("subjectId", value)}
          />
        </div>
        <div>
          <div className="flex items-center justify-between gap-2 mb-1">
            <Label className="mb-0">Related ID</Label>
            <SwitchField
              placement="start"
              value={relatedIdContactSearch}
              onValueChange={() => onToggleRelatedIdContactSearch()}
              label="Contact search"
            />
          </div>
          {relatedIdContactSearch ? (
            <ContactSearchField
              label=""
              value={filters.relatedId}
              onChange={(value) => setField("relatedId", value)}
            />
          ) : (
            <TextInput
              fullWidth
              value={filters.relatedId}
              onValueChange={(value) => setField("relatedId", value)}
            />
          )}
        </div>
        <div>
          <Label>Level</Label>
          <TextInput
            fullWidth
            type="number"
            value={filters.level}
            onValueChange={(value) => setField("level", value)}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            intent="secondary"
            label="Clear filters"
            onClick={onClear}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            size="sm"
            intent="primary"
            label="Search org model elements"
            onClick={onApply}
          >
            Search
          </Button>
        </div>
      </Collapsible.Content>
    </Collapsible>
  );
};

export default OrgModelFilters;
